import { FC } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

export type DividerType = 'horizontal' | 'vertical' | 'text';
export type DividerTextPlacement = 'left' | 'center' | 'right';

export interface DividerProps {
  // Type of divider to render
  type?: DividerType;
  // Text label displayed alongside the lines (text type only)
  text?: string;
  // Controls which side the text is pushed toward (text type only)
  textPlacement?: DividerTextPlacement;
  // Extra classes applied to the text span (text type only)
  textClassName?: string;
  // Extra classes applied to the line element(s)
  lineClassName?: string;
  // Extra classes applied to the outer wrapper
  className?: string;
}

const Divider: FC<DividerProps> = ({
  type = 'horizontal',
  text,
  textPlacement = 'center',
  textClassName,
  lineClassName,
  className,
}) => {
  // Vertical divider — use inside flex rows
  if (type === 'vertical') {
    return (
      <div
        role="separator"
        aria-orientation="vertical"
        className={twMerge('w-px self-stretch bg-mf-gray-200', className)}
      />
    );
  }

  // Text divider — horizontal line with a label
  if (type === 'text') {
    const lineClasses = twMerge('h-px bg-mf-gray-200', lineClassName);

    return (
      <div
        role="separator"
        aria-orientation="horizontal"
        className={twMerge('flex items-center gap-3', className)}
      >
        {/* Left line: short when text is left-aligned, grows otherwise */}
        <div
          className={classNames(
            lineClasses,
            textPlacement === 'left' ? 'w-6 shrink-0' : 'flex-1',
          )}
        />

        <span
          className={twMerge(
            'shrink-0 text-sm text-mf-gray-400',
            textClassName,
          )}
        >
          {text}
        </span>

        {/* Right line: short when text is right-aligned, grows otherwise */}
        <div
          className={classNames(
            lineClasses,
            textPlacement === 'right' ? 'w-6 shrink-0' : 'flex-1',
          )}
        />
      </div>
    );
  }

  // Horizontal divider (default)
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      className={twMerge('h-px w-full bg-mf-gray-200', className)}
    />
  );
};

Divider.displayName = 'Divider';

export default Divider;
